import {createAsyncThunk, createSelector, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {isEqual} from 'lodash';
import {
  ICoachingData,
  ICoachingValue,
  IResolutionStatus
} from '../../CoachingFormContainer/interfaces';
import {IStatusTypes} from '../../CoachingFormStatus/interfaces';
import {IFiltersSliceState} from '../../DrilldownDashboardContainer/filters/filtersSlice';
import {
  getIndividualSurveysData,
  loadActionAlertsAttachedStatus,
  loadAdvancedActionAlertsAccess,
  loadClassicActionAlertsAccess,
  loadIndividualSurveysStatuses,
  loadIndividualSurveysStatusTypes
} from '../IndividualSurveysDashboard/dashboardAPI';
import {RootState} from '../store';
import {surveysFilterFunction} from '../utils';

export interface IIndividualSurveysFilters {
  actionItemFilter: boolean;
  readFilter: boolean;
  unReadFilter: boolean;
  notesFilter: boolean;
  perfectScoreFilter: boolean;
  statusFilter: boolean;
  wowFilter: boolean;
  salesOpportunityFilter: boolean;
  attritionRiskFilter: boolean;
  flagFilter: boolean;
}

interface IDashboardState {
  isLoading: boolean;
  apiResponse: ICoachingData;
  individualSurveys: INormalizedDomain<ICoachingValue>
  firstLoadFulfilled: boolean;
  individualSurveysFilters: IIndividualSurveysFilters;
  individualSurveysStatusFilter: number[];
  statusTypes: IStatusTypes[];
  normalizedStatusTypes: INormalizedDomain<any>;
  statuses: IResolutionStatus[];
  legacyStatuses: INormalizedDomain<any>;
  actionAlertsAttachedStatus: any[];
  selectedIndividualSurveysIds: number[]
  apiCallLog: IAPICallStack<IApiCall>
  getQuestionsList: boolean
  advancedActionAlertsAccess: any;
  classicActionAlertsAccess: any;
}

export interface INormalizedDomain<T> {
  byId: {
    [id: string]: T
  }
}

interface IAPICallStack<T> {
  byId: {
    [id: string]: T
  }
  latestTimestamp: number
}

interface IApiCall {
  requestId: string
  timestamp: number
}

const initialState: IDashboardState = {
  isLoading: false,
  apiResponse: undefined,
  individualSurveys: {
    byId: {}
  },
  firstLoadFulfilled: false,
  individualSurveysFilters: {
    actionItemFilter: false,
    readFilter: false,
    unReadFilter: false,
    notesFilter: false,
    perfectScoreFilter: false,
    statusFilter: false,
    wowFilter: false,
    salesOpportunityFilter: false,
    attritionRiskFilter: false,
    flagFilter: false,
  },
  individualSurveysStatusFilter: [],
  statusTypes: [],
  statuses: [],
  actionAlertsAttachedStatus: [],
  selectedIndividualSurveysIds: [],
  normalizedStatusTypes: {
    byId: {},
  },
  legacyStatuses: {
    byId: {},
  },
  apiCallLog: {
    byId: {},
    latestTimestamp: Date.now()
  },
  getQuestionsList: false,
  advancedActionAlertsAccess: false,
  classicActionAlertsAccess: false,
};

export const loadIndividualSurveys = createAsyncThunk(
  'dashboard/getIndividualSurveys',
  async (filters: IFiltersSliceState) => {
    return getIndividualSurveysData(filters);
  }
);

export const getIndividualSurveysStatusTypes = createAsyncThunk(
  'dashboard/getStatusTypes',
  async () => {
    return loadIndividualSurveysStatusTypes();
  }
);

export const getIndividualSurveysStatuses = createAsyncThunk(
  'dashboard/getStatuses',
  async () => {
    return loadIndividualSurveysStatuses();
  }
);

export const getActionAlertsAttachedStatus = createAsyncThunk(
  'dashboard/getActionAlertsAttachedStatus',
  async (individualSurveys: ICoachingValue[]) => {
    return loadActionAlertsAttachedStatus(individualSurveys);
  }
);

export const getAdvancedActionAlertsAccess = createAsyncThunk(
  'dashboard/getAdvancedActionAlertsAccess',
  async () => {
    const response = await loadAdvancedActionAlertsAccess();
    return response;
  }
);

export const getClassicActionAlertsAccess = createAsyncThunk(
  'dashboard/getClassicActionAlertsAccess',
  async () => {
    const response = await loadClassicActionAlertsAccess();
    return response;
  }
);

export const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    resetFilters: (state) => {
      state.individualSurveysFilters = {
        actionItemFilter: false,
        readFilter: false,
        unReadFilter: false,
        notesFilter: false,
        perfectScoreFilter: false,
        statusFilter: false,
        wowFilter: false,
        salesOpportunityFilter: false,
        attritionRiskFilter: false,
        flagFilter: false,
      };
      state.individualSurveysStatusFilter = [];
    },
    updateDashboardState: (state: IDashboardState, action) => {
      const apiResponse = action.payload;
      if (!isEqual(state, apiResponse)) {
        state.apiResponse = apiResponse;
      }
      return state;
    },
    toggleIndividualSurveyFilter: (state: IDashboardState, action) => {
      const filterName = action.payload;
      const newValue = !state.individualSurveysFilters[filterName]

      // only toggle read/unread filters when activating the other one
      if (newValue) {
        if (filterName === 'readFilter') {
          state.individualSurveysFilters.unReadFilter = !newValue;
        } else if (filterName === 'unReadFilter') {
          state.individualSurveysFilters.readFilter = !newValue;
        }
      }

      state.individualSurveysFilters[filterName] = newValue;
    },
    updateStatusFilter: (state: IDashboardState, action) => {
      /**
       * Filtering by Individual Survey Status works by having an array of Status types Ids.
       * Status is added/removed once the user has clicked the button
       */
      const statusFilters = state.individualSurveysStatusFilter;
      const selectedStatusFilterId = action.payload;
      const searchElementIndex = statusFilters.indexOf(selectedStatusFilterId);
      if (searchElementIndex > -1) {
        statusFilters.splice(searchElementIndex, 1);
      } else {
        statusFilters.push(selectedStatusFilterId);
      }
      state.individualSurveysStatusFilter = statusFilters;
    },
    setSelectedAllIndividualInterviewsIds: (state, action) => {
      state.selectedIndividualSurveysIds = action.payload.reduce((acc, curr: ICoachingValue) => {
        return [...acc, curr.id]
      }, [])
    },
    setSelectedNoneIndividualInterviewsIds: (state) => {
      state.selectedIndividualSurveysIds = [];
    },
    toggleSelectedIndividualSurvey: (state: IDashboardState, action) => {
      const selectedIndividualSurveys = state.selectedIndividualSurveysIds;
      const selectedIndex = selectedIndividualSurveys.indexOf(action.payload.id);

      if (selectedIndex >= 0) {
        selectedIndividualSurveys.splice(selectedIndex, 1);
      } else {
        selectedIndividualSurveys.push(action.payload.id);
      }

      state.selectedIndividualSurveysIds = selectedIndividualSurveys;
    },
    markIndividualSurveyAsRead: (state: IDashboardState, action) => {
      if (state.apiResponse.values.length) {
        const recordIndex = state.apiResponse.values.findIndex((v) => v.id === action.payload.id);
        if (recordIndex >= 0) {
          state.apiResponse.values[recordIndex].viewed = 'Yes';
        }
      }
    },
    deleteIndividualSurveysSelection: (state: IDashboardState) => {
        if (state.apiResponse.values.length && state.selectedIndividualSurveysIds.length) {
            state.apiResponse.values = state.apiResponse.values.filter((v) => !state.selectedIndividualSurveysIds.includes(v.id));
            state.selectedIndividualSurveysIds = [];
        }
    },
    toggleIndividualSurveyAsActionItem: (state: IDashboardState, action: PayloadAction<ICoachingValue>) => {
      if (state.apiResponse.values.length) {
        const recordIndex = state.apiResponse.values.findIndex((v) => v.id === action.payload.id);
        if (recordIndex >= 0) {
          state.apiResponse.values[recordIndex] = action.payload;
        }
      }
    },
    updateGetQuestionsList: (state, action: PayloadAction<boolean>) => {
      state.getQuestionsList = action.payload;
    },
    toggleIndividualSurveyAsWowItem: (state: IDashboardState, action) => {
      if (state.apiResponse.values.length) {
        const recordIndex = state.apiResponse.values.findIndex((v) => v.id === action.payload.id);
        if (recordIndex >= 0) {
          if (action.payload.wow === '1') {
            state.apiResponse.values[recordIndex].wow = '0';
          } else {
            state.apiResponse.values[recordIndex].wow = '1';
          }
          // after toggling the wow flag, a survey will always have comments/notes
          state.apiResponse.values[recordIndex].hasComments = true;
        }
      }
    },
    toggleIndividualSurveyAsSalesOpportunity: (state: IDashboardState, action) => {
      if (state.apiResponse.values.length) {
        const recordIndex = state.apiResponse.values.findIndex((v) => v.id === action.payload.id);
        if (recordIndex >= 0) {
          if (action.payload.is_sales_opportunity === 1) {
            state.apiResponse.values[recordIndex].is_sales_opportunity = 0;
          } else {
            state.apiResponse.values[recordIndex].is_sales_opportunity = 1;
          }
          // after toggling the sales flag, a survey will always have comments/notes
          state.apiResponse.values[recordIndex].hasComments = true;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadIndividualSurveys.pending, (state, action) => {
      const timestamp = Date.now();
      state.isLoading = true;
      state.apiCallLog.byId[action.meta.requestId] = {
        requestId: action.meta.requestId,
        timestamp
      }
      state.apiCallLog.latestTimestamp = timestamp;
    });
    builder.addCase(loadIndividualSurveys.fulfilled, (state, action) => {
      if (state.apiCallLog.byId[action.meta.requestId].timestamp === state.apiCallLog.latestTimestamp) {
        state.apiResponse = action.payload;
        state.apiResponse.values.forEach((individualSurvey) => {
          state.individualSurveys.byId[individualSurvey.id.toString()] = individualSurvey;
        })
        state.isLoading = false;
        if (!state.firstLoadFulfilled) {
          state.firstLoadFulfilled = true;
        }
      }
    });
    builder.addCase(
      getIndividualSurveysStatusTypes.fulfilled,
      (state, action) => {
        state.statusTypes = action.payload;
        action.payload.forEach((statusType) => {
          state.normalizedStatusTypes.byId[statusType.status.toLowerCase()] = {
            id: statusType.id,
            status: statusType.status,
            color: statusType.color
          };
        })
      }
    );
    builder.addCase(getIndividualSurveysStatuses.fulfilled, (state, action) => {
      state.statuses = action.payload;
      action.payload.forEach((legacyStatus) => {
        state.legacyStatuses.byId[legacyStatus.form.toString()] = {
          id: legacyStatus.resolutionId,
          status: legacyStatus.resolutionStatus,
          color: ''
        }
      })
      return state;
    });

    builder.addCase(getActionAlertsAttachedStatus.fulfilled, (state, action) => {
      state.actionAlertsAttachedStatus = action.payload;
      return state;
    });
    builder.addCase(getAdvancedActionAlertsAccess.fulfilled, (state: IDashboardState, action) => {
      if (action.payload) {
        state.advancedActionAlertsAccess = action.payload;
        return state;
      }
    });
    builder.addCase(getClassicActionAlertsAccess.fulfilled, (state: IDashboardState, action) => {
      if (action.payload) {
        state.classicActionAlertsAccess = action.payload;
        return state;
      }
    });
  },
});

export const selectDashboardState = (state: RootState) => state.dashboard;

export const selectRecords = createSelector(
  (state) => state.dashboard,
  (dashboard) => {
    const {individualSurveysFilters} = dashboard;
    const rawRecords = dashboard.apiResponse
      ? dashboard.apiResponse.values
      : [];
    const individualSurveysStatusFilter =
      dashboard.individualSurveysStatusFilter;
    return rawRecords.filter(surveysFilterFunction(dashboard, individualSurveysFilters, individualSurveysStatusFilter));
  }
)
export const {
  toggleIndividualSurveyFilter,
  updateStatusFilter,
  setSelectedAllIndividualInterviewsIds,
  setSelectedNoneIndividualInterviewsIds,
  toggleSelectedIndividualSurvey,
  resetFilters,
  markIndividualSurveyAsRead,
  toggleIndividualSurveyAsActionItem,
  updateGetQuestionsList,
  toggleIndividualSurveyAsWowItem,
  toggleIndividualSurveyAsSalesOpportunity,
  deleteIndividualSurveysSelection,
} = dashboardSlice.actions;
export default dashboardSlice.reducer;
